import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, X } from 'lucide-react';
import { ResourceType } from '@cotera/api';
import { resourceViewOfItem } from '@cotera/sdk/core';
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { useWhoami } from '@cotera/client/app/stores/org';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@cotera/client/v0/components/ui/sidebar';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import { cn } from '@cotera/client/v0/lib/utils';
import { collapseTransition, useSidebarSectionOpen } from './sidebar-section';
import { RESOURCE_TYPE_CONFIG } from '../../resources/type/resource-type-config';
import { ResourceTypeIconBubble } from '../../resources/type/resource-type-icon-bubble';
import { resourceItemPath } from '../../resources/navigation';
import {
  favoriteRefOfItem,
  moveFavorite,
  useFavorites,
  useReorderFavorites,
  useToggleFavorite,
  type FavoriteItem,
} from '../../resources/favorites';

const FAVORITES_COOKIE_NAME = 'sidebar_favorites_state';

/**
 * Sidebar-sized version of the Library's type tile.
 *
 * The Library draws these at `h-8 w-8`, which is half again the height of a
 * sidebar row's text. Shrunk to the 20px the nav icons occupy so a favorite
 * lines up with New chat, Search and Library rather than towering over them.
 */
const FavoriteIcon: React.FC<{ item: FavoriteItem }> = ({ item }) => {
  const resourceType = item.type as ResourceType;
  const config = RESOURCE_TYPE_CONFIG[resourceType];
  // Only agents carry a custom emoji today; the tile's colour is what says
  // "agent" either way, so the emoji replaces the glyph and nothing else.
  const emoji = item.type === 'agent' ? item.data.emoji : null;
  return (
    <ResourceTypeIconBubble
      resourceType={resourceType}
      icon={config.icon}
      emoji={emoji}
      className="h-5 w-5 rounded-[5px]"
      iconClassName="h-3.5 w-3.5"
    />
  );
};

const FavoritesSkeleton: React.FC = () => (
  <SidebarMenu>
    {[1, 2, 3].map((i) => (
      <SidebarMenuItem key={i}>
        <div className="flex items-center gap-2 px-2 py-2">
          <Skeleton className="h-4 w-4 rounded-[5px] bg-coco-accent" />
          <Skeleton className="h-4 w-3/5 rounded-full bg-coco-accent" />
        </div>
      </SidebarMenuItem>
    ))}
  </SidebarMenu>
);

/**
 * One favorite. A link to the resource, its type tile, and an `×` for the one
 * action specific to being here — unstarring.
 *
 * Rows are draggable: the order is the user's, and a list of shortcuts is only
 * as useful as its top few entries, so the top few have to be arrangeable.
 */
const FavoriteRow: React.FC<{
  item: FavoriteItem;
  index: number;
  isDragging: boolean;
  /** Where a drop would land, as an insertion index; null while nothing drags. */
  dropIndex: number | null;
  onDragStart: (index: number, event: React.DragEvent<HTMLLIElement>) => void;
  onDragOverRow: (index: number, event: React.DragEvent<HTMLLIElement>) => void;
  onDrop: () => void;
  onDragEnd: () => void;
}> = ({
  item,
  index,
  isDragging,
  dropIndex,
  onDragStart,
  onDragOverRow,
  onDrop,
  onDragEnd,
}) => {
  const org = useWhoami((s) => s.org);
  const location = useLocation();
  const toggleFavorite = useToggleFavorite();
  const ref = favoriteRefOfItem(item);
  const view = resourceViewOfItem(item);
  const title = view.title();
  const path = resourceItemPath(org.slug, ref.type, ref.id);
  // Prefix, not equality: a chat's address grows a segment when one of its
  // artifacts is focused, and the favorite is still the page you are on.
  const pathname = location.pathname.replace(/\/$/, '');
  const isActive = pathname === path || pathname.startsWith(`${path}/`);

  return (
    <SidebarMenuItem
      draggable
      onDragStart={(event) => onDragStart(index, event)}
      onDragOver={(event) => onDragOverRow(index, event)}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
      onDragEnd={onDragEnd}
      className={cn(
        'relative',
        isDragging && 'opacity-40',
        // The insertion line. Drawn on the row rather than as a separate
        // element so it cannot be what the pointer is over, which would make
        // the drop target flicker as the line moved under the cursor.
        dropIndex === index &&
          'before:absolute before:inset-x-1 before:-top-px before:h-0.5 before:rounded-full before:bg-primary',
        dropIndex === index + 1 &&
          'after:absolute after:inset-x-1 after:-bottom-px after:h-0.5 after:rounded-full after:bg-primary'
      )}
    >
      <SidebarMenuButton asChild className="cursor-pointer" isActive={isActive}>
        <Link to={path} state={{ entrypoint: 'sidebar-favorites' }}>
          <FavoriteIcon item={item} />
          <span className="truncate">{title}</span>
        </Link>
      </SidebarMenuButton>
      {/* An `×`, not a `⋯`. Unfavoriting is the only thing a row here can do,
          and a menu that opens onto a single entry charges two clicks and a
          popover for one action. Linear's favorites list does the same. The
          row's other actions are not missing — they live on the resource
          itself, wherever it is opened from. */}
      {/* `asChild`, so the button itself is the hover target. Without it the
          trigger renders its own wrapper element and the action — which is
          absolutely positioned against the row — would hang off that instead,
          landing outside the row entirely. `placement="right"` puts the label
          just outside the sidebar rather than over the favorites beneath it. */}
      <Tooltip
        side="center"
        placement="right"
        tooltipContent="Unfavorite"
        asChild
      >
        <SidebarMenuAction
          showOnHover
          aria-label={`Unfavorite ${title}`}
          onClick={() => toggleFavorite(ref.type, ref.id)}
          // `size-3.5` over the primitive's `size-4`: this is a dismiss, not the
          // row's subject, and at the same weight as the type tile opposite it
          // the `×` competed with the name between them. The 20px button keeps
          // its hit area — only the glyph inside it comes down.
          className="top-1/2 -translate-y-1/2 cursor-pointer peer-data-[size=default]/menu-button:top-1/2 [&>svg]:size-3.5"
        >
          <X />
        </SidebarMenuAction>
      </Tooltip>
    </SidebarMenuItem>
  );
};

/**
 * The sidebar's Favorites section.
 *
 * Renders nothing at all when the user has starred nothing. A permanently empty
 * section is a heading over a blank space on every page load, and the ways in
 * — the star in a page's top bar, "Favorite" in every row menu — are
 * where the feature is discovered instead. This is what Linear and Notion do.
 */
export function Favorites() {
  const { items, isPending } = useFavorites();
  const reorder = useReorderFavorites();
  const [open, handleOpenChange] = useSidebarSectionOpen(FAVORITES_COOKIE_NAME);
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  const [dropIndex, setDropIndex] = React.useState<number | null>(null);

  const handleDragStart = React.useCallback(
    (index: number, event: React.DragEvent<HTMLLIElement>) => {
      setDragIndex(index);
      // A drag started on the row's icon rather than its link carries no data,
      // and Firefox refuses to start one that does not. The payload itself is
      // unused — the dragged index is held in state, since a `dataTransfer`
      // cannot be read during `dragover`, which is where the drop target is
      // worked out.
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', '');
    },
    []
  );

  const handleDragOverRow = React.useCallback(
    (index: number, event: React.DragEvent<HTMLLIElement>) => {
      if (dragIndex === null) {
        return;
      }
      // Without this the browser refuses the drop and the row snaps back.
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      const below = event.clientY - rect.top > rect.height / 2;
      setDropIndex(below ? index + 1 : index);
    },
    [dragIndex]
  );

  const handleDrop = React.useCallback(() => {
    if (dragIndex === null || dropIndex === null) {
      return;
    }
    const next = moveFavorite(items, dragIndex, dropIndex);
    setDragIndex(null);
    setDropIndex(null);
    reorder(next.map(favoriteRefOfItem));
  }, [dragIndex, dropIndex, items, reorder]);

  const handleDragEnd = React.useCallback(() => {
    setDragIndex(null);
    setDropIndex(null);
  }, []);

  if (!isPending && items.length === 0) {
    return null;
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={handleOpenChange}
      className="group/favorites"
    >
      <SidebarGroup
        className="group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:pointer-events-none"
        style={{ transition: collapseTransition('opacity') }}
      >
        {/* An explicit 11.7px, because the theme's scale has nothing between
            `text-xs` (10.5px) and `text-sm` (13px): at xs the heading read as
            fine print beside its own 13px rows, and at sm it matched them and
            stopped reading as a heading at all. Weight and the muted
            `foreground/55` do the rest of the work. */}
        <SidebarGroupLabel asChild className="shrink-0 pr-2 text-[0.9rem]">
          <CollapsibleTrigger className="w-full cursor-pointer hover:text-sidebar-foreground">
            Favorites
            <ChevronDown
              aria-hidden="true"
              className="ml-auto transition-transform duration-200 group-data-[state=closed]/favorites:-rotate-90"
            />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        {/* Capped and scrolled, the way Recents below it is.
            `SidebarContent` clips its overflow and Recents claims the leftover
            height with `flex-1`, so an uncapped Favorites simply took the room
            Recents needed and then ran past the bottom of the sheet — on a
            short screen (a phone's nav sheet is ~560px of usable height) eight
            starred items was enough to push Recents off it entirely, with no
            scroll to reach either. `dvh` rather than `%` because the section's
            own parent is `min-h-0` and has no definite height to take a
            percentage of. */}
        <CollapsibleContent className="max-h-[40dvh] overflow-y-auto no-scrollbar">
          <SidebarGroupContent>
            {isPending ? (
              <FavoritesSkeleton />
            ) : (
              <SidebarMenu>
                {items.map((item, index) => {
                  const ref = favoriteRefOfItem(item);
                  return (
                    <FavoriteRow
                      key={`${ref.type}:${ref.id}`}
                      item={item}
                      index={index}
                      isDragging={dragIndex === index}
                      dropIndex={dragIndex === null ? null : dropIndex}
                      onDragStart={handleDragStart}
                      onDragOverRow={handleDragOverRow}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                    />
                  );
                })}
              </SidebarMenu>
            )}
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}
